import React, { useState, useRef } from 'react';
import emailjs from 'emailjs-com';
import ReCAPTCHA from 'react-google-recaptcha';
import '../css/contact.css';
import { useLocation, useNavigate } from 'react-router-dom';

const ContactForm = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [captchaVerified, setCaptchaVerified] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const form = useRef();
    const recaptchaRef = useRef();

    const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const USER_ID = process.env.REACT_APP_EMAILJS_USER_ID;
    const RECAPTCHA_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    const handleCaptchaChange = (value) => {
        setCaptchaVerified(!!value);
        setErrorMessage('');
    };

    const to12Hour = (time24) => {
        if (!time24) return '';

        let [hours, minutes] = time24.split(':');
        hours = parseInt(hours, 10);

        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;

        return `${hours}:${minutes} ${ampm}`;
    };

    const sendEmail = (e) => {
        e.preventDefault();

        const formEl = form.current;
        if (!formEl) return;

        // Required field definitions
        const requiredFields = [
            { name: 'name', label: 'name' },
            { name: 'cell', label: 'cell/text number' },
            { name: 'email', label: 'email' },
            { name: 'location', label: 'location' },
            { name: 'date', label: 'date' },
            { name: 'time', label: 'time' },
            { name: 'message', label: 'message' },
            { name: 'audioRequired', label: 'audio equipment choice' },
            { name: 'lighting', label: 'lighting choice' },
        ];

        const missing = [];

        requiredFields.forEach(field => {
            const el = formEl.querySelector(`[name="${field.name}"]`);
            if (!el) return;

            if (el.type === 'radio') {
                // For radio groups, check any checked
                const groupChecked = formEl.querySelectorAll(
                    `input[name="${field.name}"]:checked`
                );
                if (groupChecked.length === 0) {
                    missing.push(field.label);
                }
            } else if (!el.value || el.value.trim() === '') {
                missing.push(field.label);
            }
        });

        if (missing.length > 0) {
            setErrorMessage(
                `Please fill out all required fields: ${missing.join(', ')}.`
            );
            return;
        }

        if (!captchaVerified) {
            setErrorMessage('Please complete the reCAPTCHA.');
            return;
        }

        // Convert time to 12-hour format
        const timeInput = formEl.querySelector('input[name="time"]');
        const time12 = to12Hour(timeInput ? timeInput.value : '');

        // Create / update hidden field for EmailJS
        let hiddenTime = formEl.querySelector('input[name="time_standard"]');
        if (!hiddenTime) {
            hiddenTime = document.createElement('input');
            hiddenTime.type = 'hidden';
            hiddenTime.name = 'time_standard';
            formEl.appendChild(hiddenTime);
        }
        hiddenTime.value = time12;

        emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, formEl, USER_ID)
            .then(
                (result) => {
                    console.log('Email successfully sent!', result.text);

                    formEl.reset();
                    if (recaptchaRef.current) {
                        recaptchaRef.current.reset();
                    }
                    setCaptchaVerified(false);
                    setErrorMessage('');
                    navigate('/thank-you');
                },
                (error) => {
                    console.error('There was an error:', error.text);
                    setErrorMessage(
                        'There was an error sending your message. Please try again.'
                    );
                }
            );
    };

    return (
        <div className="booking">
            <form ref={form} onSubmit={sendEmail}>
                <div className="contact-input-wrapper" style={{ marginBottom: '1rem' }}>
                    <label>What is your personal, company or group's name?</label>
                    <input
                        type="text"
                        name="name"
                        required
                        className="contact-input"
                        style={{ width: '100%', padding: '8px' }}
                    />
                </div>

                <div className="contact-input-wrapper" style={{ marginBottom: '1rem' }}>
                    <label>What is your cell/text number?</label>
                    <input
                        type="text"
                        name="cell"
                        required
                        className="contact-input"
                        style={{ width: '100%', padding: '8px' }}
                    />
                </div>

                <div className="contact-input-wrapper" style={{ marginBottom: '1rem' }}>
                    <label>Email:</label>
                    <input
                        type="email"
                        name="email"
                        required
                        className="contact-input"
                        style={{ width: '100%', padding: '8px' }}
                    />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                    <label>What is the location of your event?</label>
                    <input
                        type="text"
                        name="location"
                        required
                        className="contact-input"
                        style={{ width: '100%', padding: '8px' }}
                    />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                    <label>What is the date of your event?</label>
                    <input
                        type="date"
                        name="date"
                        required
                        className="contact-input"
                        style={{ width: '100%', padding: '8px' }}
                    />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                    <label>What is the start and end time of your event?</label>
                    <input
                        type="time"
                        name="time"
                        required
                        className="contact-input"
                        style={{ width: '100%', padding: '8px' }}
                    />
                </div>

                <div className="contact-input-wrapper" style={{ marginBottom: '1rem' }}>
                    <label>Tell me briefly about your event!</label>
                    <textarea
                        name="message"
                        rows="5"
                        required
                        className="contact-input"
                        style={{ width: '100%', padding: '8px' }}
                    ></textarea>
                </div>

                <div className="contact-radio-group" style={{ marginBottom: '1rem' }}>
                    <label>Will we be providing audio equipment for your event?</label>
                    <div className="contact-radio-options">
                        <label style={{ marginRight: '1rem' }}>
                            <input
                                type="radio"
                                name="audioRequired"
                                value="Yes"
                                required
                                className="contact-radio-input"
                            />{' '}
                            Yes
                        </label>
                        <label>
                            <input
                                type="radio"
                                name="audioRequired"
                                value="No"
                                className="contact-radio-input"
                            />{' '}
                            No
                        </label>
                    </div>
                </div>

                <div className="contact-radio-group" style={{ marginBottom: '1rem' }}>
                    <label>Will we be providing the lighting system?</label>
                    <div className="contact-radio-options">
                        <label style={{ marginRight: '1rem' }}>
                            <input
                                type="radio"
                                name="lighting"
                                value="Yes"
                                required
                                className="contact-radio-input"
                            />{' '}
                            Yes
                        </label>
                        <label>
                            <input
                                type="radio"
                                name="lighting"
                                value="No"
                                className="contact-radio-input"
                            />{' '}
                            No
                        </label>
                    </div>
                </div>
                <div style={{ margin: '1rem 0' }}>
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={RECAPTCHA_KEY}
                        onChange={handleCaptchaChange}
                    />
                </div>

                {errorMessage && (
                    <p style={{ color: 'red', marginBottom: '1rem' }}>
                        {errorMessage}
                    </p>
                )}

                <button
                    type="submit"
                    className={`contact-submit-button ${captchaVerified ? 'contact-submit-enabled' : 'contact-submit-disabled'}`}
                    disabled={!captchaVerified}
                >
                    Send
                </button>
            </form>
        </div>
    );
};

export default ContactForm;